import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingState,
  Select,
  Tabs,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { useBranch } from '@/features/branch/branch-context';
import { formatRM, parseRM, senToInput } from '@/features/billing/money';
import { FileButton } from '@/components/FileButton';
import { api } from '@/lib/api';
import { useUrlState } from '@/lib/url-state';
import { PageContainer } from '@/layouts/PageContainer';
import { todayKl, dayLabel } from '@/pages/calendar/time';
import { BooksForm } from './BooksForms';
import { LegalEntities } from './LegalEntities';
import styles from './parity.module.css';
import { CLINIC_TZ } from '@/lib/clinic-time';
interface Company {
  id: string;
  name: string;
  status: string;
  allowanceSen: number | null;
  employees: {
    id: string;
    fullName: string;
    employeeNo: string;
    allowanceSen: number;
    usedSen: number;
    leftSen: number;
  }[];
}
interface Settlement {
  id: string;
  settlementDate: string;
  branch: { name: string };
  method: string;
  provider: string;
  grossSen: number;
  feeSen: number;
  netSen: number;
  receiptsSen: number;
  status: string;
  note: string | null;
}
interface Journal {
  day: string;
  posted: boolean;
  lines: { account: string; debitSen: number; creditSen: number }[];
  postedDays: { day: string; postedAt: string }[];
}
interface DoctorFee {
  staffId: string;
  fullName: string;
  basis: string | null;
  rateSen: number | null;
  visits: number;
  sessions: number;
  grossSen: number;
  epfSen: number;
  netSen: number;
}
interface Einvoice {
  enabled: boolean;
  tin: string | null;
  connected: boolean;
  note: string;
}
type BookTab =
  | 'companies'
  | 'accounts'
  | 'settlements'
  | 'journal'
  | 'einvoice'
  | 'fees'
  | 'exports';
function useBook<T>(name: string, query = '', enabled = true) {
  const { selected } = useBranch();
  return useQuery({
    queryKey: ['books', name, selected?.id ?? 'all', query],
    queryFn: ({ signal }) =>
      api.get<T>(`/finance/${name}${query}`, { signal }).then((r) => r.data),
    enabled,
  });
}
function money(value: string, message: string) {
  const n = parseRM(value);
  if (n === null || n > 100_000_000) throw new Error(message);
  return n;
}
export function BooksPage() {
  const { selected } = useBranch();
  return <BooksContent key={selected?.id ?? 'all'} />;
}
function BooksContent() {
  const { t } = useTranslation();
  const [tab, setTab] = useUrlState<BookTab>('tab', 'companies', [
    'companies',
    'accounts',
    'settlements',
    'journal',
    'einvoice',
    'fees',
    'exports',
  ]);
  return (
    <PageContainer>
      <Tabs
        variant="bar"
        label={t('books.title')}
        value={tab}
        onChange={setTab}
        items={(
          [
            'companies',
            'accounts',
            'settlements',
            'journal',
            'einvoice',
            'fees',
            'exports',
          ] as BookTab[]
        ).map((value) => ({
          value,
          label: t(value === 'journal' ? 'books.accounting' : `books.${value}`),
        }))}
      />
      {tab === 'companies' ? (
        <LegalEntities />
      ) : tab === 'accounts' ? (
        <CompanyAccounts />
      ) : tab === 'settlements' ? (
        <Settlements />
      ) : tab === 'journal' ? (
        <Accounting />
      ) : tab === 'einvoice' ? (
        <EInvoice />
      ) : tab === 'fees' ? (
        <DoctorFees />
      ) : (
        <Exports />
      )}
    </PageContainer>
  );
}
function CompanyAccounts() {
  const { t } = useTranslation();
  const q = useBook<Company[]>('companies');
  const { hasPermission } = useSession();
  const [editing, setEditing] = useState<Company | null>(null);
  if (q.isLoading) return <LoadingState />;
  if (q.error)
    return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  return (
    <>
      {!q.data?.length && (
        <Card title={t('books.accounts')}>
          <EmptyState title={t('operations.noRecords')} />
        </Card>
      )}
      {q.data?.map((c) => (
        <Card
          key={c.id}
          title={c.name}
          flush
          actions={
            hasPermission('books:manage') &&
            c.status === 'active' && (
              <Button size="sm" onClick={() => setEditing(c)}>
                {t('books.addEmployee')}
              </Button>
            )
          }
        >
          <DataTable
            caption={c.name}
            rows={c.employees}
            rowKey={(e) => e.id}
            columns={[
              {
                key: 'name',
                header: t('books.employee'),
                render: (e) => (
                  <>
                    <strong>{e.fullName}</strong>
                    <span className={styles.sub}>{e.employeeNo}</span>
                  </>
                ),
              },
              {
                key: 'allowance',
                header: t('books.allowance'),
                align: 'right',
                render: (e) => formatRM(e.allowanceSen),
              },
              {
                key: 'used',
                header: t('books.used'),
                align: 'right',
                render: (e) => formatRM(e.usedSen),
              },
              {
                key: 'left',
                header: t('books.left'),
                align: 'right',
                render: (e) => formatRM(e.leftSen),
              },
            ]}
          />
        </Card>
      ))}
      {editing && (
        <BooksForm
          title={t('books.addEmployee')}
          path={`/finance/companies/${editing.id}/employees`}
          onClose={() => setEditing(null)}
          fields={[
            {
              key: 'fullName',
              label: t('books.employee'),
              value: '',
              maxLength: 160,
            },
            {
              key: 'employeeNo',
              label: t('books.employeeNo'),
              value: '',
              maxLength: 40,
            },
            {
              key: 'allowance',
              label: t('books.allowanceRM'),
              value:
                editing.allowanceSen === null
                  ? ''
                  : senToInput(editing.allowanceSen),
              required: false,
            },
          ]}
          build={(v) => {
            if (v.fullName.trim().length < 2 || !v.employeeNo.trim())
              throw new Error(t('books.invalidEmployee'));
            return {
              fullName: v.fullName.trim(),
              employeeNo: v.employeeNo.trim(),
              ...(v.allowance.trim()
                ? { allowanceSen: money(v.allowance, t('books.invalidAmount')) }
                : {}),
            };
          }}
        />
      )}
    </>
  );
}
function Settlements() {
  const { t } = useTranslation();
  const q = useBook<Settlement[]>('settlements');
  const { hasPermission } = useSession();
  const { selected } = useBranch();
  const [editing, setEditing] = useState<Settlement | null>(null);
  return (
    <>
      <p className={styles.notice}>{t('books.settlementBody')}</p>
      <Card title={t('books.settlementTitle')} flush>
        <DataTable
          caption={t('books.settlementTitle')}
          rows={q.data}
          rowKey={(r) => r.id}
          loading={q.isLoading}
          error={q.error}
          onRetry={() => void q.refetch()}
          columns={[
            {
              key: 'day',
              header: t('books.day'),
              render: (r) => (
                <>
                  {dayLabel(r.settlementDate)}
                  <span className={styles.sub}>{r.branch.name}</span>
                </>
              ),
            },
            {
              key: 'provider',
              header: t('books.provider'),
              render: (r) => (
                <>
                  {r.provider}
                  <span className={styles.sub}>{r.method}</span>
                </>
              ),
            },
            {
              key: 'receipts',
              header: t('books.receipts'),
              align: 'right',
              render: (r) => formatRM(r.receiptsSen),
            },
            {
              key: 'gross',
              header: t('books.gross'),
              align: 'right',
              render: (r) => formatRM(r.grossSen),
            },
            {
              key: 'fee',
              header: t('books.fee'),
              align: 'right',
              render: (r) => formatRM(r.feeSen),
            },
            {
              key: 'net',
              header: t('books.net'),
              align: 'right',
              render: (r) => formatRM(r.netSen),
            },
            {
              key: 'status',
              header: t('operations.status'),
              render: (r) => (
                <>
                  <Badge tone={r.status === 'matched' ? 'success' : 'warning'}>
                    {r.status}
                  </Badge>
                  {r.note && <span className={styles.sub}>{r.note}</span>}
                </>
              ),
            },
            {
              key: 'action',
              header: '',
              render: (r) =>
                hasPermission('books:manage') &&
                selected &&
                r.status !== 'matched' && (
                  <Button size="sm" onClick={() => setEditing(r)}>
                    {t('books.investigate')}
                  </Button>
                ),
            },
          ]}
        />
      </Card>
      {editing && (
        <BooksForm
          title={t('books.investigate')}
          path={`/finance/settlements/${editing.id}/investigate`}
          onClose={() => setEditing(null)}
          fields={[
            {
              key: 'text',
              label: t('books.note'),
              value: editing.note ?? '',
              maxLength: 1000,
            },
          ]}
          build={(v) => {
            if (!v.text.trim()) throw new Error(t('books.reasonRequired'));
            return { text: v.text.trim() };
          }}
        />
      )}
    </>
  );
}
function Accounting() {
  const { t } = useTranslation();
  const { selected } = useBranch();
  const { hasPermission } = useSession();
  const [day, setDay] = useState(todayKl());
  const q = useBook<Journal>('journal', `?day=${day}`, !!selected && !!day);
  const [posting, setPosting] = useState(false);
  return (
    <>
      <div className={styles.toolbar}>
        <Field label={t('books.day')}>
          <Input
            type="date"
            value={day}
            onChange={(e) => setDay(e.target.value)}
          />
        </Field>
      </div>
      <p className={styles.notice}>{t('books.accountingBody')}</p>
      <Card
        title={t('books.journal')}
        flush
        actions={
          <>
            {q.data && (
              <Badge>
                {t(q.data.posted ? 'books.posted' : 'books.preview')}
              </Badge>
            )}
            {hasPermission('books:manage') &&
              selected &&
              q.data &&
              !q.data.posted && (
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => setPosting(true)}
                >
                  {t('books.postJournal')}
                </Button>
              )}
          </>
        }
      >
        <DataTable
          caption={t('books.journal')}
          rows={selected ? q.data?.lines : undefined}
          rowKey={(r) => r.account}
          loading={q.isLoading}
          error={q.error}
          onRetry={() => void q.refetch()}
          empty={
            <EmptyState
              title={t(
                selected ? 'operations.noRecords' : 'operations.chooseBranch',
              )}
            />
          }
          columns={[
            {
              key: 'account',
              header: t('books.account'),
              render: (r) => r.account,
            },
            {
              key: 'debit',
              header: t('books.debit'),
              align: 'right',
              render: (r) => formatRM(r.debitSen),
            },
            {
              key: 'credit',
              header: t('books.credit'),
              align: 'right',
              render: (r) => formatRM(r.creditSen),
            },
          ]}
        />
      </Card>
      {selected && q.data && (
        <Card title={t('books.postedDays')} flush>
          <DataTable
            caption={t('books.postedDays')}
            rows={q.data.postedDays}
            rowKey={(r) => r.day}
            columns={[
              {
                key: 'day',
                header: t('books.day'),
                render: (r) => dayLabel(r.day),
              },
              {
                key: 'posted',
                header: t('books.posted'),
                render: (r) =>
                  new Date(r.postedAt).toLocaleString('en-GB', {
                    timeZone: CLINIC_TZ,
                  }),
              },
            ]}
          />
        </Card>
      )}
      {posting && (
        <BooksForm
          title={t('books.postJournal')}
          path="/finance/journal/post"
          onClose={() => setPosting(false)}
          fields={[
            { key: 'day', label: t('books.day'), value: day, type: 'date' },
          ]}
          build={(v) => ({ day: v.day })}
        />
      )}
    </>
  );
}
function EInvoice() {
  const { t } = useTranslation();
  const q = useBook<Einvoice>('einvoice');
  const { hasPermission } = useSession();
  const [editing, setEditing] = useState(false);
  if (q.isLoading) return <LoadingState />;
  if (q.error)
    return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const d = q.data;
  if (!d) return null;
  return (
    <Card
      title={t('books.einvoiceTitle')}
      actions={
        <Badge tone={d.connected ? 'success' : 'warning'}>
          {t(d.connected ? 'books.connected' : 'books.disconnected')}
        </Badge>
      }
    >
      <dl className={styles.kv}>
        <dt>{t('operations.status')}</dt>
        <dd>{t(d.enabled ? 'books.enabled' : 'books.disabled')}</dd>
        <dt>{t('books.tin')}</dt>
        <dd>{d.tin ?? t('operations.notConfigured')}</dd>
      </dl>
      <p className={styles.notice}>{d.note}</p>
      <p className={styles.notice}>{t('books.einvoiceBody')}</p>
      {hasPermission('books:manage') && (
        <Button size="sm" onClick={() => setEditing(true)}>
          {t('books.configure')}
        </Button>
      )}
      {editing && (
        <BooksForm
          title={t('books.einvoiceTitle')}
          path="/finance/einvoice"
          onClose={() => setEditing(false)}
          fields={[
            {
              key: 'enabled',
              label: t('operations.status'),
              value: String(d.enabled),
              options: [
                { value: 'false', label: t('books.disabled') },
                { value: 'true', label: t('books.enabled') },
              ],
            },
            {
              key: 'tin',
              label: t('books.tin'),
              value: d.tin ?? '',
              required: false,
              maxLength: 20,
            },
          ]}
          build={(v) => {
            const tin = v.tin.trim();
            if ((v.enabled === 'true' || tin) && tin.length < 5)
              throw new Error(t('books.invalidTin'));
            return { enabled: v.enabled === 'true', ...(tin ? { tin } : {}) };
          }}
        />
      )}
    </Card>
  );
}
function DoctorFees() {
  const { t } = useTranslation();
  const [month, setMonth] = useState(todayKl().slice(0, 7));
  const q = useBook<DoctorFee[]>('doctor-fees', `?month=${month}`, !!month);
  const { hasPermission } = useSession();
  const [editing, setEditing] = useState<DoctorFee | null>(null);
  return (
    <>
      <Field label={t('books.month')}>
        <Input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
        />
      </Field>
      <Card
        title={t('books.fees')}
        flush
        actions={
          <FileButton
            path={`/finance/payroll.csv?month=${month}`}
            name={`payroll-${month}.csv`}
            label={t('books.payroll')}
            disabled={!month}
          />
        }
      >
        <DataTable
          caption={t('books.fees')}
          rows={q.data}
          rowKey={(r) => r.staffId}
          loading={q.isLoading}
          error={q.error}
          onRetry={() => void q.refetch()}
          columns={[
            {
              key: 'doctor',
              header: t('books.doctor'),
              render: (r) => <strong>{r.fullName}</strong>,
            },
            {
              key: 'basis',
              header: t('books.basis'),
              render: (r) => r.basis ?? t('operations.notConfigured'),
            },
            {
              key: 'rate',
              header: t('books.rate'),
              align: 'right',
              render: (r) => (r.rateSen === null ? '—' : formatRM(r.rateSen)),
            },
            {
              key: 'work',
              header: t('books.work'),
              render: (r) => `${r.visits} / ${r.sessions}`,
            },
            {
              key: 'gross',
              header: t('books.gross'),
              align: 'right',
              render: (r) => (r.basis ? formatRM(r.grossSen) : '—'),
            },
            {
              key: 'epf',
              header: t('books.epf'),
              align: 'right',
              render: (r) => (r.basis ? formatRM(r.epfSen) : '—'),
            },
            {
              key: 'net',
              header: t('books.net'),
              align: 'right',
              render: (r) => (r.basis ? formatRM(r.netSen) : '—'),
            },
            {
              key: 'edit',
              header: '',
              render: (r) =>
                hasPermission('books:manage') && (
                  <Button size="sm" onClick={() => setEditing(r)}>
                    {t('books.editBasis')}
                  </Button>
                ),
            },
          ]}
        />
      </Card>
      <p className={styles.notice}>{t('books.payrollBody')}</p>
      {editing && (
        <BooksForm
          title={t('books.editBasis')}
          path={`/finance/doctor-fees/${editing.staffId}`}
          onClose={() => setEditing(null)}
          fields={[
            {
              key: 'basis',
              label: t('books.basis'),
              value: editing.basis ?? 'employed',
              options: ['employed', 'sessional', 'locum'].map((value) => ({
                value,
                label: t(`books.${value}`),
              })),
            },
            {
              key: 'rate',
              label: t('books.rateRM'),
              value:
                editing.rateSen === null ? '' : senToInput(editing.rateSen),
            },
            {
              key: 'epf',
              label: t('books.epfPercent'),
              value: '',
              type: 'number',
              min: 0,
              max: 30,
              required: false,
            },
          ]}
          build={(v) => {
            const epf = v.epf.trim() ? Number(v.epf) : null;
            if (epf !== null && (!Number.isInteger(epf) || epf < 0 || epf > 30))
              throw new Error(t('books.invalidEpf'));
            return {
              basis: v.basis,
              rateSen: money(v.rate, t('books.invalidAmount')),
              ...(epf !== null ? { epfPct: epf } : {}),
            };
          }}
        />
      )}
    </>
  );
}
function Exports() {
  const { t } = useTranslation();
  const [month, setMonth] = useState(todayKl().slice(0, 7));
  const [kind, setKind] = useState('sales');
  return (
    <Card title={t('books.exports')}>
      <div className={styles.toolbar}>
        <Field label={t('books.month')}>
          <Input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
        </Field>
        <Field label={t('books.data')}>
          <Select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            options={['sales', 'receipts', 'claims', 'stock'].map((value) => ({
              value,
              label: t(`books.export_${value}`),
            }))}
          />
        </Field>
        <FileButton
          path={`/finance/exports?month=${month}&kind=${kind}`}
          name={`${kind}-${month}.csv`}
          label={t('books.buildCsv')}
          disabled={!month}
        />
      </div>
    </Card>
  );
}
